import { apiService } from './api';

export interface DuplicateCandidate {
  candidate_id: string;
  customer1_id: string;
  customer2_id: string;
  // Enriched fields from endpoint
  customer1_name?: string;
  customer2_name?: string;
  customer1_phone?: string;
  customer2_phone?: string;
  customer1_email?: string;
  customer2_email?: string;
  match_score: number;
  match_criteria: {
    email_match?: boolean;
    phone_match?: boolean;
    full_name_match?: boolean;
    name_similarity: number;
    first_name_similarity: number;
    last_name_similarity: number;
    dob_match?: boolean;
    total_score: number;
  };
  // A = phone and/or email match, B = exact full-name match, C = fuzzy
  // single-name match only — see api/app/core/duplicate_match.py.
  match_tier: 'A' | 'B' | 'C';
  status: 'PENDING' | 'REVIEWED' | 'MERGED' | 'DISMISSED';
  reviewed_by_user_id?: string;
  reviewed_date?: string;
  notes?: string;
  create_time: string;
}

export interface MergeLog {
  merge_id: string;
  master_customer_id: string;
  merged_customer_id: string;
  merged_customer_data: any;
  merge_reason?: string;
  merged_by_user_id?: string;
  merge_date: string;
}

export interface CustomerMergeRequest {
  master_customer_id: string;
  duplicate_customer_id: string;
  merge_reason?: string;
  // Per-field override: key = field name, value = "master" | "duplicate"
  field_overrides?: Record<string, 'master' | 'duplicate'>;
}

export interface DuplicateReviewRequest {
  notes?: string;
}

export interface Customer {
  customer_id: string;
  first_name: string;
  last_name?: string;
  email?: string;
  phone?: string;
  alternative_phone?: string;
  note?: string;
  date_of_birth?: string;
  source: 'ADMIN' | 'ECOMMERCE' | 'MERGED';
  status: 'ACTIVE' | 'INACTIVE' | 'DUPLICATE' | 'MERGED';
  master_customer_id?: string;
  ecommerce_user_id?: string;
  is_email_verified: boolean;
  is_phone_verified: boolean;
  preferred_contact_method?: string;
  marketing_consent: boolean;
  last_login?: string;
  last_order_date?: string;
  create_time: string;
  update_time: string;
  full_name: string;
  is_duplicate: boolean;
  is_merged: boolean;
  is_active: boolean;
}

class CustomerMergeService {
  /**
   * Get all potential duplicate candidates
   */
  async getDuplicateCandidates(params?: {
    skip?: number;
    limit?: number;
    min_score?: number;
    status?: string;
  }): Promise<DuplicateCandidate[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.min_score !== undefined) queryParams.append('min_score', params.min_score.toString());
    if (params?.status) queryParams.append('status', params.status);

    const response: any = await apiService.get(`/customers/duplicates?${queryParams.toString()}`);
    return response.data || response;
  }

  /**
   * Find potential duplicates for a specific customer
   */
  async getCustomerDuplicates(customerId: string, minScore: number = 0.7): Promise<DuplicateCandidate[]> {
    const response: any = await apiService.get(`/customers/${customerId}/duplicates?min_score=${minScore}`);
    return response.data || response;
  }

  /**
   * Merge duplicate customer into master customer
   */
  async mergeCustomers(mergeRequest: CustomerMergeRequest): Promise<Customer> {
    const response: any = await apiService.post('/customers/merge', mergeRequest);
    return response.data || response;
  }

  /**
   * Dismiss a duplicate candidate
   */
  async dismissDuplicateCandidate(candidateId: string, reviewRequest: DuplicateReviewRequest): Promise<void> {
    await apiService.post(`/customers/duplicates/${candidateId}/dismiss`, reviewRequest);
  }

  /**
   * Get merge history for a customer
   */
  async getCustomerMergeHistory(customerId: string): Promise<MergeLog[]> {
    const response: any = await apiService.get(`/customers/${customerId}/merge-history`);
    return response.data || response;
  }

  /**
   * Scan entire database for duplicates
   */
  async scanForDuplicates(minScore: number = 0.8): Promise<{
    message: string;
    candidates_found: number;
    min_score_used: number;
  }> {
    const response: any = await apiService.post(`/customers/scan-duplicates?min_score=${minScore}`);
    return response.data || response;
  }

  /**
   * Get customer details with enhanced information
   */
  async getCustomerWithDetails(customerId: string): Promise<Customer> {
    const response: any = await apiService.get(`/customers/${customerId}`);
    return response.data || response;
  }

  /**
   * Display info for the coarse A/B/C duplicate-confidence tier. A phone
   * or email match is common for shared-household phone lines in
   * Bangladesh — flagged as top priority for review, not treated as
   * certain the two records are the same person.
   */
  getTierInfo(tier: DuplicateCandidate['match_tier']): {
    label: string;
    color: 'error' | 'warning' | 'info';
    tooltip: string;
  } {
    switch (tier) {
      case 'A':
        return {
          label: 'A',
          color: 'error',
          tooltip: 'Phone and/or email match — the strongest signal, but shared household lines are common in Bangladesh. Still verify before merging.',
        };
      case 'B':
        return {
          label: 'B',
          color: 'warning',
          tooltip: 'Exact full name match — a solid signal, though common names can coincide.',
        };
      case 'C':
      default:
        return {
          label: 'C',
          color: 'info',
          tooltip: 'Similar name only (fuzzy match) — worth a glance, not a confident match.',
        };
    }
  }

  /**
   * Calculate match score display information
   */
  getMatchScoreInfo(score: number): {
    level: 'high' | 'medium' | 'low';
    color: 'error' | 'warning' | 'info';
    description: string;
  } {
    if (score >= 0.9) {
      return {
        level: 'high',
        color: 'error',
        description: 'Very likely duplicate - immediate review recommended'
      };
    } else if (score >= 0.7) {
      return {
        level: 'medium',
        color: 'warning',
        description: 'Possible duplicate - review recommended'
      };
    } else {
      return {
        level: 'low',
        color: 'info',
        description: 'Low similarity - may not be duplicate'
      };
    }
  }

  /**
   * Format match criteria for display
   */
  formatMatchCriteria(criteria: DuplicateCandidate['match_criteria']): string[] {
    const matches: string[] = [];

    if (criteria.email_match) {
      matches.push('✓ Email matches exactly');
    }

    if (criteria.phone_match) {
      matches.push('✓ Phone number matches');
    }

    if (criteria.full_name_match) {
      matches.push('✓ Full name matches exactly');
    }

    if (criteria.dob_match) {
      matches.push('✓ Date of birth matches');
    }

    if (criteria.name_similarity > 0.8) {
      matches.push(`✓ Name similarity: ${Math.round(criteria.name_similarity * 100)}%`);
    } else if (criteria.name_similarity > 0.6) {
      matches.push(`~ Name similarity: ${Math.round(criteria.name_similarity * 100)}%`);
    }

    if (criteria.first_name_similarity > 0.9) {
      matches.push(`✓ First name: ${Math.round(criteria.first_name_similarity * 100)}% match`);
    }

    if (criteria.last_name_similarity > 0.9) {
      matches.push(`✓ Last name: ${Math.round(criteria.last_name_similarity * 100)}% match`);
    }

    return matches;
  }

  /**
   * Get customer source display information
   */
  getCustomerSourceInfo(source: Customer['source']): {
    label: string;
    color: 'primary' | 'secondary' | 'success';
    icon: string;
  } {
    switch (source) {
      case 'ADMIN':
        return {
          label: 'Admin Created',
          color: 'primary',
          icon: 'admin_panel_settings'
        };
      case 'ECOMMERCE':
        return {
          label: 'Self Registered',
          color: 'success',
          icon: 'shopping_cart'
        };
      case 'MERGED':
        return {
          label: 'Merged Account',
          color: 'secondary',
          icon: 'merge'
        };
      default:
        return {
          label: 'Unknown',
          color: 'secondary',
          icon: 'help'
        };
    }
  }

  /**
   * Get customer status display information
   */
  getCustomerStatusInfo(status: Customer['status']): {
    label: string;
    color: 'success' | 'warning' | 'error' | 'info';
    icon: string;
  } {
    switch (status) {
      case 'ACTIVE':
        return {
          label: 'Active',
          color: 'success',
          icon: 'check_circle'
        };
      case 'INACTIVE':
        return {
          label: 'Inactive',
          color: 'warning',
          icon: 'pause_circle'
        };
      case 'DUPLICATE':
        return {
          label: 'Duplicate',
          color: 'error',
          icon: 'content_copy'
        };
      case 'MERGED':
        return {
          label: 'Merged',
          color: 'info',
          icon: 'merge'
        };
      default:
        return {
          label: 'Unknown',
          color: 'info',
          icon: 'help'
        };
    }
  }
}

export const customerMergeService = new CustomerMergeService();
